import React from "react";
import { motion } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1758518730037-a16581a040e8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const Values = () => {
  const { t } = useLanguage();

  return (
    <section
      id="values"
      data-testid="values-section"
      className="relative py-12 md:py-16 overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, #154C57 0%, #1F6B7A 55%, #2C7A8C 100%)",
      }}
    >
      {/* Decorative blobs */}
      <div
        aria-hidden
        className="absolute -top-32 -end-32 h-96 w-96 rounded-full"
        style={{ background: "rgba(232,163,101,0.18)", filter: "blur(60px)" }}
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -start-20 h-[28rem] w-[28rem] rounded-full"
        style={{ background: "rgba(255,255,255,0.06)", filter: "blur(70px)" }}
      />
      {/* Subtle grid texture */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse at center, black 30%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, black 30%, transparent 80%)",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid gap-12 lg:grid-cols-12 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div
                className="absolute -inset-3 rounded-[28px] -z-10"
                style={{ background: "rgba(232,163,101,0.25)" }}
              />
              <img
                src={ABOUT_IMG}
                alt="Consulting"
                className="w-full rounded-[24px] shadow-[0_30px_70px_-20px_rgba(0,0,0,0.45)]"
              />
            </div>
          </motion.div>

          {/* Content */}
          <div className="lg:col-span-7">
            <motion.span
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5 }}
              className="eyebrow"
              data-testid="values-eyebrow"
              style={{ color: "#F4C499" }}
            >
              {t.values.eyebrow}
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="display display-2 mt-4 text-white"
            >
              {t.values.title}
            </motion.h2>

            <div className="mt-10 grid gap-5 sm:grid-cols-2">
              {t.values.points.map((p, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.45, delay: i * 0.08 }}
                  className="rounded-2xl border-s-2 ps-5 py-3 pe-2"
                  style={{ borderColor: "#F4C499" }}
                  data-testid={`value-point-${i}`}
                >
                  <div className="text-sm font-semibold" style={{ color: "#F4C499" }}>
                    {p.k}
                  </div>
                  <div className="mt-1 text-[15px] leading-relaxed text-white/85">
                    {p.v}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Values;
